import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Alert } from 'react-native';
import styled from 'styled-components/native';
import FeedbackButton from '@/components/FeedbackButton';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import BadgedIcon from '@/assets/icon/ic_badged_crew.svg';
import MoreIcon from '@/assets/icon/ic_more.svg';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { RECOMMEND_MOBILITY_TOOL_LABELS, SPACIOUS_LABELS, } from '@/constant/review';
import { useUpvoteToggle } from '@/hooks/useUpvoteToggle';
import ImageList from '@/screens/PlaceDetailScreen/components/PlaceDetailImageList';
import ToastUtils from '@/utils/ToastUtils';
import { usePlaceDetailScreenName } from '@/hooks/useFeatureFlags';
import useNavigation from '@/navigation/useNavigation';
import UserMobilityLabel from '@/screens/PlaceDetailScreen/components/UserMobilityLabel';
import { useDeleteReview } from '@/screens/PlaceDetailScreen/hooks/useDeleteReview';
import DeleteBottomSheet from '@/screens/PlaceDetailScreen/modals/DeleteBottomSheet';
import SccPressable from './SccPressable';
export default function PlaceReviewItem({ placeId, review, variant = 'detail', }) {
    var _a;
    const navigation = useNavigation();
    const pdpScreen = usePlaceDetailScreenName();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const isDetailReview = variant === 'detail';
    const isHistoryReview = variant === 'history';
    const targetId = isDetailReview
        ? review.id
        : review.placeReviewId;
    const deletePlaceReview = useDeleteReview({
        type: 'place',
        reviewId: targetId,
        placeId,
    });
    const { isUpvoted, totalUpvoteCount, toggleUpvote } = useUpvoteToggle({
        initialIsUpvoted: review.isUpvoted,
        initialTotalCount: review.totalUpvoteCount,
        targetId,
        targetType: 'PLACE_REVIEW',
        placeId,
    });
    const reviewText = review.comment;
    const reviewDate = dayjs(review.createdAt.value).format('YYYY.MM.DD');
    const handleLinkPress = () => {
        if (variant === 'history' && isHistoryReview) {
            navigation.navigate(pdpScreen, {
                placeInfo: {
                    placeId,
                },
            });
        }
    };
    const renderHeader = () => {
        var _a, _b, _c;
        if (variant === 'detail' && isDetailReview) {
            return (_jsxs(HeaderLeft, { variant: variant, children: [_jsx(ReviewerName, { children: ((_a = review.user) === null || _a === void 0 ? void 0 : _a.nickname) || '익명' }), ((_b = review.user) === null || _b === void 0 ? void 0 : _b.isClubMember) && _jsx(BadgedIcon, {}), ((_c = review.user) === null || _c === void 0 ? void 0 : _c.isClubMember) &&
                        review.mobilityTool !== 'NONE' && (_jsx(ReviewDate, { children: "\u00B7" })), review.mobilityTool !== 'NONE' && (_jsx(UserMobilityLabel, { mobilityTool: review.mobilityTool }))] }));
        }
        if (variant === 'history' && isHistoryReview) {
            return (_jsx(SccPressable, { elementName: "place_detail_link", onPress: handleLinkPress, children: _jsxs(HeaderLeft, { variant: variant, children: [_jsx(PlaceName, { children: review.placeName }), _jsx(PlaceAddress, { children: review.placeAddress })] }) }));
        }
        return null;
    };
    return (_jsxs(Container, { children: [_jsxs(HeaderRow, { variant: variant, children: [renderHeader(), review.isDeletable && (_jsx(SccTouchableOpacity, { elementName: "place_review_more_button", onPress: () => Alert.alert('더보기', '', [
                            {
                                text: '삭제',
                                onPress: () => setIsDeleteModalVisible(true),
                                style: 'destructive',
                            },
                            {
                                text: '수정',
                                onPress: () => ToastUtils.show('준비중입니다.'),
                            },
                            { text: '취소', style: 'cancel' },
                        ], { cancelable: true }), children: _jsx(MoreIcon, {}) }))] }), review.images && review.images.length > 0 && (_jsx(ImageList, { images: review.images, roundCorners: true })), _jsxs(ReviewContentColumn, { children: [_jsxs(ReviewInfoColumn, { children: [_jsxs(ReviewInfoRow, { children: [_jsx(ReviewInfoLabel, { children: "\uB4F1\uB85D\uC77C" }), _jsx(ReviewInfoValue, { children: reviewDate })] }), _jsxs(ReviewInfoRow, { children: [_jsx(ReviewInfoLabel, { children: "\uCD94\uCC9C\uB300\uC0C1" }), _jsx(ReviewInfoValue, { children: (_a = review.recommendedMobilityTypes) === null || _a === void 0 ? void 0 : _a.map(type => RECOMMEND_MOBILITY_TOOL_LABELS[type]).join(', ') })] }), _jsxs(ReviewInfoRow, { children: [_jsx(ReviewInfoLabel, { children: "\uB0B4\uBD80\uACF5\uAC04" }), _jsx(ReviewInfoValue, { children: SPACIOUS_LABELS[review.spaciousType] })] })] }), reviewText && (_jsxs(_Fragment, { children: [_jsx(ReviewText, { numberOfLines: isExpanded ? undefined : 2, children: reviewText }), reviewText.length > 50 && ( // TODO 정확히 2줄 넘으면 안보이게 하기
                            _jsx(ExpandButton, { elementName: "place_review_expand_button", onPress: () => setIsExpanded(value => !value), children: _jsx(ExpandButtonText, { children: isExpanded ? '접기' : '더보기' }) }))] }))] }), _jsx(FeedbackButton, { isUpvoted: isUpvoted, total: totalUpvoteCount, onPressUpvote: toggleUpvote, onPressAnalytics: () => navigation.navigate('UpvoteAnalytics', {
                    targetType: 'PLACE_REVIEW',
                    targetId,
                }) }), _jsx(DeleteBottomSheet, { isVisible: isDeleteModalVisible, confirmText: '리뷰를 정말 삭제할까요?', onPressCancelButton: () => setIsDeleteModalVisible(false), onPressConfirmButton: () => {
                    setIsDeleteModalVisible(false);
                    deletePlaceReview.mutate();
                } })] }));
}
// styled-components
const Container = styled.View `
  gap: 16px;
  flex-direction: column;
`;
const HeaderRow = styled.View `
  flex-direction: row;
  gap: 4px;
  align-items: ${props => props.variant === 'history' ? 'flex-start' : 'center'};
  justify-content: space-between;
`;
const HeaderLeft = styled.View `
  flex-direction: ${props => (props.variant === 'history' ? 'column' : 'row')};
  gap: 4px;
  align-items: ${props => props.variant === 'history' ? 'flex-start' : 'center'};
  flex: ${props => (props.variant === 'history' ? '1' : 'none')};
`;
const ReviewerName = styled.Text `
  font-size: 15px;
  line-height: 22px;
  font-family: ${font.pretendardBold};
  color: ${color.gray100};
`;
const PlaceName = styled.Text `
  font-size: 16px;
  line-height: 24px;
  font-family: ${font.pretendardBold};
  color: ${color.gray90};
`;
const PlaceAddress = styled.Text `
  font-size: 13px;
  line-height: 18px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray40};
`;
const ReviewDate = styled.Text `
  font-size: 11px;
  line-height: 14px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray50};
`;
const ReviewContentColumn = styled.View `
  flex-direction: column;
  gap: 8px;
`;
const ReviewInfoColumn = styled.View `
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;
const ReviewInfoRow = styled.View `
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;
const ReviewInfoLabel = styled.Text `
  font-size: 12px;
  line-height: 16px;
  font-family: ${font.pretendardRegular};
  width: 46px;
  color: ${color.gray40};
`;
const ReviewInfoValue = styled.Text `
  font-size: 14px;
  line-height: 22px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray70};
`;
const ReviewText = styled.Text `
  font-size: 15px;
  line-height: 22px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray70};
`;
const ExpandButton = styled(SccTouchableOpacity) `
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
`;
const ExpandButtonText = styled.Text `
  font-size: 13px;
  line-height: 18px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray40};
`;
